import { useEthPrice } from '@components/Web3/useEthPrice'
import { QueryClient, useQuery } from '@tanstack/react-query'
import { MINUTE_MS } from 'data/constants'
import { gql, request } from 'graphql-request'
import {
    calculateCombinedUsdValue,
    format18DecimalAsUsd,
} from 'hooks/useFormattedNormalizedTipAmount'
import { ETH_ADDRESS, SpaceAddressFromSpaceId, TipParams, useBaseReads } from 'use-towns-client'

// GraphQL query for fetching tip leaderboard
const TIP_LEADERBOARD_QUERY = gql`
    query GetTipLeaderboard($spaceId: String!, $limit: Int!) {
        tipLeaderboards(
            where: { spaceId: $spaceId }
            orderBy: "totalSent"
            orderDirection: "desc"
            limit: $limit
        ) {
            items {
                user
                totalSent
                totalSentUSDC
            }
        }
    }
`

export type TipLeaderboardData = {
    leaderboard: Record<string, { totalEthSent: string; totalUsdcSent: string }>
    lastUpdatedAt: number
}

export interface TipLeaderboardItem {
    user: string
    totalSent: string
    totalSentUSDC: string
}

interface TipLeaderboardResponse {
    tipLeaderboards: {
        items: TipLeaderboardItem[]
    }
}

/**
 * Shared function to fetch tip leaderboard items from GraphQL.
 * Used by both useTipLeaderboard (limit: 100) and useUserRank (limit: 3).
 */
export const fetchTipLeaderboardItems = async (
    spaceAddress: string,
    subgraphEndpoint: string | undefined,
    limit: number,
): Promise<TipLeaderboardItem[]> => {
    if (!subgraphEndpoint) {
        console.error('[fetchTipLeaderboardItems] No subgraph endpoint available')
        return []
    }

    try {
        const data = await request<TipLeaderboardResponse>(
            subgraphEndpoint,
            TIP_LEADERBOARD_QUERY,
            {
                spaceId: spaceAddress.toLowerCase(),
                limit,
            },
        )

        return data?.tipLeaderboards?.items ?? []
    } catch (error) {
        console.error('[fetchTipLeaderboardItems] Error fetching tip leaderboard:', error)
        return []
    }
}

export const queryKeyTipLeaderboard = (
    spaceAddress: string | undefined,
    currency = ETH_ADDRESS,
) => ['tipLeaderboard', spaceAddress ?? null, currency]

export const optimisticallyUpdateTipLeaderboard = (qc: QueryClient, tip: TipParams) => {
    const { senderAddress: userAddress, amount, currency } = tip
    if (tip.type === 'any') {
        return
    }
    const spaceAddress = SpaceAddressFromSpaceId(tip.spaceId)
    return qc.setQueryData(
        queryKeyTipLeaderboard(spaceAddress, currency),
        (old: TipLeaderboardData | undefined) => {
            const isETH = currency.toLowerCase() === ETH_ADDRESS.toLowerCase()
            if (!old) {
                return {
                    leaderboard: {
                        [userAddress]: {
                            totalEthSent: isETH ? amount.toString() : '0',
                            totalUsdcSent: isETH ? '0' : amount.toString(),
                        },
                    },
                    lastUpdatedAt: Date.now(),
                }
            }
            return {
                ...old,
                leaderboard: {
                    ...old.leaderboard,
                    [userAddress]: {
                        totalEthSent: isETH
                            ? (
                                  BigInt(old.leaderboard[userAddress].totalEthSent || '0') + amount
                              ).toString()
                            : old.leaderboard[userAddress].totalEthSent,
                        totalUsdcSent: isETH
                            ? old.leaderboard[userAddress].totalUsdcSent
                            : (
                                  BigInt(old.leaderboard[userAddress].totalUsdcSent || '0') + amount
                              ).toString(),
                    },
                },
                lastUpdatedAt: Date.now(),
            }
        },
    )
}

const buildRankedLeaderboard = (
    leaderboard: TipLeaderboardData['leaderboard'],
    ethPrice: string,
): { userAddress: string; totalTipped: string; rank: number }[] => {
    // Calculate USD value for each user
    const entriesWithUsd = Object.entries(leaderboard).map(
        ([userAddress, { totalEthSent, totalUsdcSent }]) => ({
            userAddress,
            usdValue: calculateCombinedUsdValue({
                ethAmount: BigInt(totalEthSent || '0'),
                usdcAmount: BigInt(totalUsdcSent || '0'),
                ethPrice,
            }),
        }),
    )

    // Sort by USD value descending
    entriesWithUsd.sort((a, b) => (b.usdValue > a.usdValue ? 1 : b.usdValue < a.usdValue ? -1 : 0))

    // Build ranked list with formatted USD
    let currentRank = 0
    let lastUsdValue = -1n

    return entriesWithUsd.map(({ userAddress, usdValue }) => {
        // If its a tie with the previous user, dont increment the rank
        if (usdValue !== lastUsdValue) {
            currentRank++
            lastUsdValue = usdValue
        }
        return {
            userAddress,
            totalTipped: format18DecimalAsUsd(usdValue),
            rank: currentRank,
        }
    })
}

export const fetchTipLeaderboard = async (
    spaceAddress: string,
    subgraphEndpoint: string | undefined,
): Promise<TipLeaderboardData> => {
    const items = await fetchTipLeaderboardItems(spaceAddress, subgraphEndpoint, 100)

    // Transform array to Record<address, amount> format
    const leaderboard = items.reduce(
        (acc, item) => {
            // acc[item.user] = item.totalSent
            acc[item.user] = {
                totalEthSent: item.totalSent,
                totalUsdcSent: item.totalSentUSDC,
            }
            return acc
        },
        {} as TipLeaderboardData['leaderboard'],
    )

    return {
        leaderboard,
        lastUpdatedAt: Date.now(),
    }
}

export function useTipLeaderboard(spaceAddress: string | undefined) {
    const { subgraphEndpoint } = useBaseReads()
    const { data: ethPrice } = useEthPrice()

    return useQuery({
        queryKey: queryKeyTipLeaderboard(spaceAddress),
        queryFn: () => {
            if (!spaceAddress) {
                return { leaderboard: {}, lastUpdatedAt: 0 }
            }
            return fetchTipLeaderboard(spaceAddress, subgraphEndpoint)
        },
        enabled: !!spaceAddress && !!subgraphEndpoint && !!ethPrice,
        staleTime: 1 * MINUTE_MS,
        refetchInterval: MINUTE_MS,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        select: (data) => ({
            ...data,
            leaderboard: buildRankedLeaderboard(data.leaderboard, ethPrice ?? '0'),
        }),
    })
}
